import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Container, Spinner, Alert, Form, Badge, Image, Button, Tabs, Tab, Modal, Table } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { getMatchHandicap } from '../utils/handicap';
import { getYouTubeEmbedUrl } from '../utils/youtube';
import { getLevelChipClass, getLevelModifierClass } from '../utils/levelColors';
import { isWalkover, getWalkoverLabel } from '../utils/walkover';

const API_URL = process.env.REACT_APP_API_URL;

const LEVEL_MAP = {
  8: 'H',
  7: 'G',
  1: 'F',
  2: 'E',
  3: 'D',
  4: 'C',
  5: 'B',
  6: 'A',
};

const getRoundName = (roundType, roundId) => {
  if (Number(roundType) === 1) {
    return `Vòng ${roundId}`;
  }

  const knockoutNames = {
    2: 'Vòng 1/16',
    3: 'Vòng 1/8',
    4: 'Tứ Kết',
    5: 'Bán Kết',
    6: 'Chung Kết',
  };
  return knockoutNames[Number(roundType)] || 'Vòng Đấu Khác';
};

const TeamLogo = ({ logo, name }) => {
  const [error, setError] = React.useState(false);

  if (error || !logo) return null;

  return (
    <Image
      src={logo}
      roundedCircle
      width={44}
      height={44}
      className="match-team-logo"
      onError={() => setError(true)}
    />
  );
};

const MatchCard = ({ match, onShowDetails }) => {
  const hasResult =
    match.home_result !== null &&
    match.home_result !== undefined &&
    match.guest_result !== null &&
    match.guest_result !== undefined;
  const isFinalized = Number(match.status) === 1;
  const matchDate = new Date(match.play_date);
  const scheduleId = match.id || match.schedule_id;
  const roundLabel = getRoundName(match.round_type, match.round_id);

  const handleCardClick = () => {
    if (!hasResult) return;
    onShowDetails(scheduleId, match);
  };

  return (
    <article
      className={`match-result-card ${hasResult ? 'match-result-card-clickable' : ''}`}
      onClick={handleCardClick}
      role={hasResult ? 'button' : undefined}
      tabIndex={hasResult ? 0 : undefined}
      onKeyDown={(event) => {
        if (hasResult && (event.key === 'Enter' || event.key === ' ')) {
          event.preventDefault();
          handleCardClick();
        }
      }}
    >
      <div className="match-card-meta">
        <span>
          {matchDate.toLocaleDateString('vi-VN', {
            weekday: 'short',
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
          })}
        </span>
        <strong>{match.table_name || roundLabel}</strong>
        <Badge className={`match-status-badge ${hasResult ? 'is-finished' : 'is-upcoming'}`}>
          {isFinalized ? 'Hoàn tất' : hasResult ? 'Có kết quả' : 'Sắp tới'}
        </Badge>
      </div>

      <div className="match-card-teams">
        <Link
          to={`/teams/${match.home}`}
          className="match-team match-team-home"
          onClick={(event) => event.stopPropagation()}
        >
          <TeamLogo logo={match.home_logo} name={match.home_name} />
          <span>{match.home_name}</span>
        </Link>

        <div className={hasResult ? 'match-card-score' : 'match-card-vs'}>
          {hasResult ? `${match.home_result} - ${match.guest_result}` : 'VS'}
        </div>

        <Link
          to={`/teams/${match.guest}`}
          className="match-team match-team-away"
          onClick={(event) => event.stopPropagation()}
        >
          <TeamLogo logo={match.guest_logo} name={match.guest_name} />
          <span>{match.guest_name}</span>
        </Link>
      </div>

      <div className="match-card-foot">
        <span>{roundLabel}</span>
        {hasResult && <strong>Xem chi tiết</strong>}
      </div>
    </article>
  );
};

const idsEqual = (a, b) => String(a) === String(b);

const MatchResults = () => {
  const [loading, setLoading] = useState(true);
  const [loadingSchedules, setLoadingSchedules] = useState(false);
  const [error, setError] = useState(null);

  const [availableYears, setAvailableYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState('');

  const [seasons, setSeasons] = useState([]);
  const [allSchedules, setAllSchedules] = useState([]);
  const [standings, setStandings] = useState([]);

  const [selectedLevelId, setSelectedLevelId] = useState(null);
  const [selectedTableId, setSelectedTableId] = useState('all');
  const [selectedRoundId, setSelectedRoundId] = useState('all');
  const [detailedMatches, setDetailedMatches] = useState({});
  const [showMatchDetailModal, setShowMatchDetailModal] = useState(false);
  const [currentMatchDetails, setCurrentMatchDetails] = useState(null);
  const [currentMatchInfo, setCurrentMatchInfo] = useState(null);
  const [allPlayersLevelHistoryMap, setAllPlayersLevelHistoryMap] = useState({});
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [currentVideoUrl, setCurrentVideoUrl] = useState('');

  useEffect(() => {
    setSelectedTableId('all');
    setSelectedRoundId('all');
  }, [selectedLevelId]);

  useEffect(() => {
    setSelectedRoundId('all');
  }, [selectedTableId]);


  const getLevelNameFromId = useCallback(
    (levelId) => LEVEL_MAP[levelId] || `ID ${levelId}`,
    []
  );

  const getPlayerLevelAtDate = useCallback((playerLevelHistory, matchDate, currentLevelId) => {
    const matchTimestamp = new Date(matchDate).getTime();
    const sortedHistory = [...playerLevelHistory].sort(
      (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
    );

    if (sortedHistory.length === 0) return currentLevelId;

    let historicalLevelId = currentLevelId;
    for (const levelChange of sortedHistory) {
      const changeTimestamp = new Date(levelChange.created_at).getTime();
      if (changeTimestamp <= matchTimestamp) {
        historicalLevelId = levelChange.level_new;
      } else {
        break;
      }
    }

    if (
      historicalLevelId === currentLevelId &&
      sortedHistory.length > 0 &&
      new Date(sortedHistory[0].created_at).getTime() > matchTimestamp
    ) {
      historicalLevelId = sortedHistory[0].level_old;
    }

    return historicalLevelId;
  }, []);

  const handleShowMatchDetails = useCallback(async (scheduleId, matchInfo) => {
    const hasResult =
      matchInfo?.home_result !== null &&
      matchInfo?.home_result !== undefined &&
      matchInfo?.guest_result !== null &&
      matchInfo?.guest_result !== undefined;
    if (!scheduleId || !hasResult) return;

    setShowMatchDetailModal(true);
    setCurrentMatchDetails(null);
    setCurrentMatchInfo({
      ...matchInfo,
      scheduleId,
      isTeamHome: true,
      homeTeamName: matchInfo.home_name,
      guestTeamName: matchInfo.guest_name,
    });

    if (detailedMatches[scheduleId]) {
      setCurrentMatchDetails(detailedMatches[scheduleId]);
      return;
    }

    try {
      const response = await axios.get(`${API_URL}/details/match-details/${scheduleId}`);
      const details = response.data || [];
      setDetailedMatches((prevDetails) => ({
        ...prevDetails,
        [scheduleId]: details,
      }));
      setCurrentMatchDetails(details);

      const uniquePlayerIds = new Set();
      details.forEach((detail) => {
        [detail.player_1, detail.player_2, detail.player_3, detail.player_4].forEach((pid) => {
          if (pid && !allPlayersLevelHistoryMap[pid]) {
            uniquePlayerIds.add(pid);
          }
        });
      });

      if (uniquePlayerIds.size > 0) {
        const levelHistories = await Promise.all(
          Array.from(uniquePlayerIds).map((pid) =>
            axios
              .get(`${API_URL}/playerspublic/${pid}/level-history`)
              .then((res) => ({ playerId: pid, history: res.data }))
              .catch(() => ({ playerId: pid, history: [] }))
          )
        );

        const historyMap = levelHistories.reduce((acc, { playerId, history }) => {
          acc[playerId] = history;
          return acc;
        }, {});

        setAllPlayersLevelHistoryMap((prev) => ({ ...prev, ...historyMap }));
      }
    } catch (err) {
      console.error(`Error fetching detailed matches for schedule ${scheduleId}:`, err);
      setCurrentMatchDetails([]);
    }
  }, [detailedMatches, allPlayersLevelHistoryMap]);

  const handleCloseMatchDetailModal = useCallback(() => {
    setShowMatchDetailModal(false);
    setCurrentMatchDetails(null);
    setCurrentMatchInfo(null);
  }, []);

  useEffect(() => {
    const fetchSeasons = async () => {
      try {
        setLoading(true);
        const res = await axios.get(`${API_URL}/seasons`);
        const allSeasons = res.data || [];
        setSeasons(allSeasons);

        const years = [
          ...new Set(
            allSeasons
              .map(s => s?.date_start ? new Date(s.date_start).getFullYear() : null)
              .filter(Boolean)
          )
        ].sort((a, b) => b - a);

        setAvailableYears(years);
        setSelectedYear(years.length > 0 ? String(years[0]) : '');
      } catch {
        setError('Không thể tải danh sách mùa giải.');
      } finally {
        setLoading(false);
      }
    };
    fetchSeasons();
  }, []);

  useEffect(() => {
    if (!selectedYear || seasons.length === 0) return;

    const fetchStandings = async () => {
      try {
        setLoading(true);
        setError(null);

        const seasonForYear = seasons.find(
          s => s?.date_start && new Date(s.date_start).getFullYear().toString() === selectedYear
        );

        if (!seasonForYear) {
          setStandings([]);
          return;
        }

        const standingsRes = await axios.get(`${API_URL}/public/league/standings?season_id=${seasonForYear.id}`);
        const standingsData = standingsRes.data || [];
        setStandings(standingsData);

        if (standingsData.length > 0) {
          setSelectedLevelId(current => current || standingsData[0].level_id);
        }
      } catch {
        setError(`Không thể tải dữ liệu cho năm ${selectedYear}.`);
        setStandings([]);
      } finally {
        setLoading(false);
      }
    };

    fetchStandings();
  }, [selectedYear, seasons]);

  const fetchSchedules = useCallback(async (seasonId, levelId) => {
    try {
      setLoadingSchedules(true);

      const params = {
        season_id: seasonId,
        level_team_id: levelId
      };

      const res = await axios.get(`${API_URL}/schedules`, { params });
      setAllSchedules(res.data || []);

    } catch (err) {
      console.error('Error fetching schedules:', err);
      setAllSchedules([]);
    } finally {
      setLoadingSchedules(false);
    }
  }, []);

  useEffect(() => {
    if (!selectedYear || seasons.length === 0 || !selectedLevelId) return;

    const seasonForYear = seasons.find(
      s => s?.date_start && new Date(s.date_start).getFullYear().toString() === selectedYear
    );

    if (!seasonForYear) return;

    const timeoutId = setTimeout(() => {
      fetchSchedules(seasonForYear.id, selectedLevelId);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [selectedYear, seasons, selectedLevelId, fetchSchedules]);

  const allLevels = useMemo(
    () => Array.isArray(standings) ? standings : [],
    [standings]
  );

  const allTables = useMemo(() => {
    const level = allLevels.find(l => idsEqual(l.level_id, selectedLevelId));
    return level?.tables || [];
  }, [allLevels, selectedLevelId]);

  const knockoutSchedules = useMemo(() => {
    return allSchedules.filter(s => Number(s.round_type) > 1);
  }, [allSchedules]);

  const groupSchedules = useMemo(() => {
    let filtered = allSchedules.filter(s => Number(s.round_type) === 1);

    if (selectedTableId !== 'all') {
      filtered = filtered.filter(s => idsEqual(s.table_id, selectedTableId));
    }

    if (selectedRoundId !== 'all') {
      filtered = filtered.filter(s => idsEqual(s.round_id, selectedRoundId));
    }

    return filtered;
  }, [allSchedules, selectedTableId, selectedRoundId]);

  const availableRounds = useMemo(() => {
    const baseGroupSchedules = allSchedules.filter(s => Number(s.round_type) === 1);

    let filtered = baseGroupSchedules;
    if (selectedTableId !== 'all') {
      filtered = filtered.filter(s => idsEqual(s.table_id, selectedTableId));
    }

    const roundMap = new Map();
    filtered.forEach(s => {
      const key = String(s.round_id || '');
      if (!roundMap.has(key)) {
        const displayName = getRoundName(s.round_type, s.round_id);
        roundMap.set(key, {
          round_id: s.round_id,
          round_type: s.round_type,
          round_name: displayName
        });
      }
    });

    return Array.from(roundMap.values()).sort((a, b) =>
      Number(a.round_id || 0) - Number(b.round_id || 0)
    );
  }, [allSchedules, selectedTableId]);

  const groupStageRounds = useMemo(() => {
    const groups = groupSchedules
      .reduce((acc, match) => {
        const key = match.table_id || match.table_name || 'unknown';
        const name = match.table_name || 'Chưa có bảng';
        if (!acc[key]) acc[key] = { name, matches: [] };
        acc[key].matches.push(match);
        return acc;
      }, {});
    return Object.values(groups);
  }, [groupSchedules]);

  const knockoutRounds = useMemo(() => {
    const byRound = knockoutSchedules
      .reduce((acc, match) => {
        const rt = Number(match.round_type);
        if (!acc[rt]) acc[rt] = { round_type: rt, name: getRoundName(rt, match.round_id), matches: [] };
        acc[rt].matches.push(match);
        return acc;
      }, {});
    return Object.values(byRound).sort((a, b) => b.round_type - a.round_type);
  }, [knockoutSchedules]);

  const handlePrevYear = () => {
    const idx = availableYears.indexOf(parseInt(selectedYear));
    if (idx < availableYears.length - 1) {
      setSelectedYear(String(availableYears[idx + 1]));
    }
  };

  const handleNextYear = () => {
    const idx = availableYears.indexOf(parseInt(selectedYear));
    if (idx > 0) {
      setSelectedYear(String(availableYears[idx - 1]));
    }
  };

  const scheduleStats = useMemo(() => {
    const finished = allSchedules.filter(
      s =>
        s.home_result !== null &&
        s.home_result !== undefined &&
        s.guest_result !== null &&
        s.guest_result !== undefined
    ).length;
    const upcoming = allSchedules.length - finished;
    return {
      total: allSchedules.length,
      finished,
      upcoming,
      knockout: knockoutSchedules.length,
    };
  }, [allSchedules, knockoutSchedules]);

  const hasActiveFilters = selectedTableId !== 'all' || selectedRoundId !== 'all';

  const resetFilters = () => {
    setSelectedTableId('all');
    setSelectedRoundId('all');
  };

  const getDetailHistoricalLevel = (detail, playerKey, levelKey) => {
    const playerId = detail[playerKey];
    const matchDate = currentMatchInfo?.play_date;
    return playerId && matchDate
      ? getLevelNameFromId(
        getPlayerLevelAtDate(
          allPlayersLevelHistoryMap[playerId] || [],
          matchDate,
          detail[levelKey]
        )
      )
      : getLevelNameFromId(detail[levelKey]);
  };

  const getDetailHandicap = (detail) => {
    if (isWalkover(detail?.walkover)) return null;
    return getMatchHandicap(
      [
        getDetailHistoricalLevel(detail, 'player_1', 'p1_level'),
        getDetailHistoricalLevel(detail, 'player_2', 'p2_level')
      ].filter(Boolean),
      [
        getDetailHistoricalLevel(detail, 'player_3', 'p3_level'),
        getDetailHistoricalLevel(detail, 'player_4', 'p4_level')
      ].filter(Boolean)
    );
  };

  const renderHandicapBadge = (handicap, side) => handicap?.side === side ? (
    <Badge bg="warning" text="dark" className="ms-1 fw-semibold" style={{ fontSize: '0.68rem', borderRadius: '5px' }} title="Chấp theo hạng">
      {handicap.value}
    </Badge>
  ) : null;

  const renderPlayerCell = (detail, playerKey, nameKey, levelKey, pointKey) => {
    const playerId = detail[playerKey];
    const historicalLevel = getDetailHistoricalLevel(detail, playerKey, levelKey);

    if (isWalkover(detail?.walkover)) {
      if (playerKey === 'player_1' || playerKey === 'player_3') {
        return (
          <Badge bg="warning" text="dark" title={getWalkoverLabel(detail.walkover)}>
            Bỏ cuộc
          </Badge>
        );
      }
      return <span className="text-muted">-</span>;
    }

    if (!playerId) {
      return detail[nameKey] || '-';
    }

    return (
      <div>
        <Link
          to={`/players/${playerId}`}
          style={{
            textDecoration: 'none',
            fontWeight: '600',
            color: 'inherit',
            transition: 'color 0.2s ease'
          }}
          onMouseEnter={(e) => e.currentTarget.style.color = 'var(--primary)'}
          onMouseLeave={(e) => e.currentTarget.style.color = 'inherit'}
        >
          {detail[nameKey] || '-'}
        </Link>
        <div className="d-flex align-items-center mt-1 match-detail-player-meta">
          <span className={`${getLevelChipClass(historicalLevel)} me-1`}>
            {historicalLevel || '?'}
          </span>
          <span className="text-muted">
            {detail[pointKey] !== null && detail[pointKey] !== undefined
              ? detail[pointKey]
              : '-'}
          </span>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <Container className="text-center mt-5">
        <Spinner animation="border" />
        <p>Đang tải dữ liệu...</p>
      </Container>
    );
  }

  if (error) {
    return <Alert variant="danger">{error}</Alert>;
  }

  const renderLoadingBlock = () => (
    <div className="match-schedule-loading">
      <Spinner animation="border" size="sm" />
      <p>Đang tải...</p>
    </div>
  );

  const renderMatchSection = (title, description, groups, emptyMessage) => (
    <section className="match-schedule-section">
      <div className="match-section-head">
        <div>
          <h2>{title}</h2>
          <p>{description}</p>
        </div>
        <span>{groups.reduce((sum, group) => sum + group.matches.length, 0)} trận</span>
      </div>

      {loadingSchedules ? (
        renderLoadingBlock()
      ) : groups.length > 0 ? (
        <div className="match-round-list">
          {groups.map(group => (
            <div className="match-round-block" key={group.name}>
              <div className="match-round-head">
                <strong>{group.name}</strong>
                <span>{group.matches.length} trận</span>
              </div>
              <div className="match-card-grid">
                {group.matches.map(match => (
                  <MatchCard key={match.id} match={match} onShowDetails={handleShowMatchDetails} />
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <Alert variant="info" className="match-empty-state">
          {emptyMessage}
        </Alert>
      )}
    </section>
  );

  const renderFilters = () => (
    <div className="match-filter-bar">
      <div className="match-filter-title">
        <span>Bộ lọc vòng bảng</span>
        {hasActiveFilters && (
          <Button variant="link" onClick={resetFilters}>
            Xóa lọc
          </Button>
        )}
      </div>
      <div className="match-filter-grid">
        <Form.Group controlId="tableFilter">
          <Form.Label>Bảng</Form.Label>
          <Form.Select
            value={selectedTableId}
            onChange={e => setSelectedTableId(e.target.value)}
            disabled={allTables.length === 0 || loadingSchedules}
          >
            <option value="all">Tất cả</option>
            {allTables.map(table => (
              <option key={table.table_id} value={table.table_id}>
                {table.table_name}
              </option>
            ))}
          </Form.Select>
        </Form.Group>
        <Form.Group controlId="roundFilter">
          <Form.Label>Vòng đấu</Form.Label>
          <Form.Select
            value={selectedRoundId}
            onChange={e => setSelectedRoundId(e.target.value)}
            disabled={availableRounds.length === 0 || loadingSchedules}
          >
            <option value="all">Tất cả</option>
            {availableRounds.map(round => (
              <option key={round.round_id} value={round.round_id}>
                {round.round_name}
              </option>
            ))}
          </Form.Select>
        </Form.Group>
      </div>
    </div>
  );

  const renderContent = () => (
    <>
      {renderMatchSection(
        'Vòng Loại Trực Tiếp',
        'Các trận knock-out theo vòng đấu.',
        knockoutRounds,
        'Không có dữ liệu vòng loại trực tiếp cho lựa chọn này.'
      )}
      {renderFilters()}
      {renderMatchSection(
        'Lịch Thi Đấu Vòng Bảng',
        'Theo bảng và vòng đấu đã chọn.',
        groupStageRounds,
        'Không có dữ liệu lịch thi đấu vòng bảng cho lựa chọn này.'
      )}
    </>
  );

  return (
    <Container className="match-results-page py-4">
      <div className="match-results-hero">
        <div className="match-results-copy">
          <p className="client-kicker">Lịch thi đấu</p>
          <h1>Lịch Thi Đấu & Kết Quả</h1>
          <p>Chi tiết các lượt trận theo mùa giải, hạng đấu, bảng và vòng.</p>
        </div>

        {availableYears.length > 0 && (
          <div className="match-year-panel">
            <span>Mùa giải</span>
            <div className="match-year-control">
              <Button
                variant="light"
                onClick={handlePrevYear}
                disabled={availableYears.indexOf(parseInt(selectedYear)) === availableYears.length - 1}
                aria-label="Năm trước"
              >
                ‹
              </Button>
              <strong>{selectedYear}</strong>
              <Button
                variant="light"
                onClick={handleNextYear}
                disabled={availableYears.indexOf(parseInt(selectedYear)) === 0}
                aria-label="Năm sau"
              >
                ›
              </Button>
            </div>
            <small>{loadingSchedules ? 'Đang cập nhật lịch...' : `${scheduleStats.total} trận trong lựa chọn`}</small>
          </div>
        )}
      </div>

      <div className="match-stat-grid">
        <span><strong>{scheduleStats.total}</strong>Tổng trận</span>
        <span><strong>{scheduleStats.finished}</strong>Đã xong</span>
        <span><strong>{scheduleStats.upcoming}</strong>Sắp tới</span>
        <span><strong>{scheduleStats.knockout}</strong>Knock-out</span>
      </div>

      {selectedLevelId && (
        <Tabs
          activeKey={selectedLevelId}
          onSelect={(k) => setSelectedLevelId(k)}
          className="match-level-tabs custom-tabs-minimal"
        >
          {allLevels.map(level => (
            <Tab
              key={level.level_id}
              eventKey={level.level_id}
              title={level.level_name}
              tabClassName={getLevelModifierClass(level.level_name)}
            >
              {renderContent()}
            </Tab>
          ))}
        </Tabs>
      )}

      <Modal
        show={showMatchDetailModal}
        onHide={handleCloseMatchDetailModal}
        size="xl"
        centered
        className="match-detail-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title className="fw-bold">
            Chi tiết trận đấu
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4">
          {currentMatchInfo && (
            <div className="match-detail-summary mb-3">
              <div>
                <span>Đội nhà</span>
                <strong>{currentMatchInfo.homeTeamName || currentMatchInfo.home_name}</strong>
              </div>
              <div className="match-detail-score">
                {currentMatchInfo.home_result} - {currentMatchInfo.guest_result}
              </div>
              <div>
                <span>Đội khách</span>
                <strong>{currentMatchInfo.guestTeamName || currentMatchInfo.guest_name}</strong>
              </div>
            </div>
          )}

          {currentMatchDetails ? (
            currentMatchDetails.length > 0 ? (
              <>
                <div className="table-responsive match-detail-table-wrap">
                  <Table striped hover className="mb-0 match-detail-table">
                    <thead>
                      <tr>
                        <th className="text-center" style={{ width: '80px' }}>Trận</th>
                        <th>VĐV 1 (Nhà)</th>
                        <th>VĐV 2 (Nhà)</th>
                        <th>VĐV 3 (Khách)</th>
                        <th>VĐV 4 (Khách)</th>
                        <th className="text-center" style={{ width: '120px' }}>Kết quả</th>
                        <th className="text-center" style={{ width: '80px' }}>Video</th>
                      </tr>
                    </thead>
                    <tbody>
                      {currentMatchDetails.map((detail, detailIndex) => {
                        const isHomeSubMatchWin = detail.home_result > detail.guest_result;
                        const handicap = getDetailHandicap(detail);

                        return (
                          <tr key={detail.id || detailIndex}>
                            <td className="text-center fw-bold">
                              <Badge bg="secondary" className="px-3 py-2">
                                #{detail.math_index}
                              </Badge>
                            </td>
                            <td>
                              <div className="d-flex align-items-start">
                                {renderPlayerCell(detail, 'player_1', 'p1_name', 'p1_level', 'point_player_1')}
                                {renderHandicapBadge(handicap, 'home')}
                              </div>
                            </td>
                            <td>{renderPlayerCell(detail, 'player_2', 'p2_name', 'p2_level', 'point_player_2')}</td>
                            <td>
                              <div className="d-flex align-items-start">
                                {renderPlayerCell(detail, 'player_3', 'p3_name', 'p3_level', 'point_player_3')}
                                {renderHandicapBadge(handicap, 'guest')}
                              </div>
                            </td>
                            <td>{renderPlayerCell(detail, 'player_4', 'p4_name', 'p4_level', 'point_player_4')}</td>
                            <td className="text-center">
                              <Badge
                                bg={isHomeSubMatchWin ? 'success' : 'danger'}
                                className="px-3 py-2"
                              >
                                {detail.home_result} - {detail.guest_result}
                              </Badge>
                              {isWalkover(detail.walkover) && (
                                <div className="small text-muted mt-1">
                                  {getWalkoverLabel(detail.walkover)}
                                </div>
                              )}
                            </td>
                            <td className="text-center">
                              {detail.video ? (
                                <Button
                                  variant="primary"
                                  size="sm"
                                  onClick={() => {
                                    setCurrentVideoUrl(detail.video);
                                    setShowVideoModal(true);
                                  }}
                                  title="Xem video"
                                >
                                  Xem
                                </Button>
                              ) : (
                                <span className="text-muted">-</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </div>

                <div className="match-detail-mobile-list">
                  {currentMatchDetails.map((detail, detailIndex) => {
                    const isHomeSubMatchWin = detail.home_result > detail.guest_result;
                    const handicap = getDetailHandicap(detail);
                    return (
                      <div className="match-detail-mobile-card" key={detail.id || detailIndex}>
                        <div className="match-detail-mobile-head">
                          <Badge bg="secondary">#{detail.math_index}</Badge>
                          <Badge bg={isHomeSubMatchWin ? 'success' : 'danger'}>
                            {detail.home_result} - {detail.guest_result}
                          </Badge>
                        </div>
                        {isWalkover(detail.walkover) && (
                          <div className="small text-muted mb-2">
                            {getWalkoverLabel(detail.walkover)}
                          </div>
                        )}
                        <div className="match-detail-mobile-grid">
                          <div>
                            <span>Đội nhà</span>
                            {renderPlayerCell(detail, 'player_1', 'p1_name', 'p1_level', 'point_player_1')}
                            {renderPlayerCell(detail, 'player_2', 'p2_name', 'p2_level', 'point_player_2')}
                          </div>
                          <div>
                            <span>Đội khách</span>
                            <div className="d-flex align-items-start">
                              {renderPlayerCell(detail, 'player_3', 'p3_name', 'p3_level', 'point_player_3')}
                              {renderHandicapBadge(handicap, 'guest')}
                            </div>
                            {renderPlayerCell(detail, 'player_4', 'p4_name', 'p4_level', 'point_player_4')}
                          </div>
                        </div>
                        {detail.video && (
                          <Button
                            variant="primary"
                            size="sm"
                            onClick={() => {
                              setCurrentVideoUrl(detail.video);
                              setShowVideoModal(true);
                            }}
                          >
                            Xem video
                          </Button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </>
            ) : (
              <Alert variant="info" className="mb-0 border-0">
                Không tìm thấy chi tiết trận con.
              </Alert>
            )
          ) : (
            <div className="text-center py-5">
              <Spinner animation="border" className="mb-3" />
              <p>Đang tải chi tiết trận đấu...</p>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseMatchDetailModal}>
            Đóng
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showVideoModal}
        onHide={() => {
          setShowVideoModal(false);
          setCurrentVideoUrl('');
        }}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title className="fw-bold">Video trận đấu</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-0">
          {(() => {
            if (!currentVideoUrl) {
              return null;
            }
            const embedUrl = getYouTubeEmbedUrl(currentVideoUrl);
            if (embedUrl) {
              return (
                <div className="ratio ratio-16x9">
                  <iframe
                    src={embedUrl}
                    title="Video trận đấu"
                    allowFullScreen
                  />
                </div>
              );
            }
            return (
              <div className="text-center py-4">
                <p className="text-muted">Link video không hỗ trợ xem trực tiếp</p>
                <a href={currentVideoUrl} target="_blank" rel="noopener noreferrer" className="d-block mb-3">{currentVideoUrl}</a>
                <Button variant="primary" onClick={() => window.open(currentVideoUrl, '_blank')}>Mở trong tab mới</Button>
              </div>
            );
          })()}
        </Modal.Body>
      </Modal>
    </Container>
  );
};

export default MatchResults;
